/* ============================================================
   Quote Request Form — 4-step flow + conditional collections

   Step 1  Q1 Product type  -> collection panel updates live
   Step 2  Q2 Quantity  (carries the MOQ disclaimer)
   Step 3  Q3 Timeline
   Step 4  Your details -> submit

   Step order is read from data attributes in the Liquid
   (data-qr-step / data-qr-answer-key / data-qr-auto), so the steps
   can be reordered there without editing this file.

   One <form>, one POST. Steps are shown/hidden, never removed, so
   Shopify's native contact form still receives every field.

   Analytics: pushes qr_step_view / qr_step_complete / qr_submit to
   dataLayer so drop-off per question is measurable in GTM/GA4.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'qrForm:';

  /* ---------------- field validation ----------------
     type="email" only requires an @, so "sam@company" passes the browser
     but bounces in real life. This insists on a dotted domain.
  */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

  /* Australian numbers only. Checked after stripping spaces, dashes,
     brackets and dots, so people can type whatever way they like.
       0412 345 678 / +61 412 345 678   mobiles
       02 9876 5432 / (07) 3123 4567    landlines (02, 03, 04, 07, 08)
       1300 123 456 / 1800 123 456      business numbers
       13 12 34                         short business numbers
  */
  var AU_PHONE_RE = /^(?:(?:\+?61|0)[23478]\d{8}|(?:\+?61)?1[38]00\d{6}|(?:\+?61)?13\d{4})$/;

  function customMessage(field) {
    var kind = field.dataset.qrValidate;
    if (!kind) return '';

    var value = (field.value || '').trim();
    if (!value) return ''; // `required` already covers empties

    if (kind === 'email') {
      return EMAIL_RE.test(value) ? '' : field.dataset.qrMessage || 'Enter a valid email address.';
    }

    if (kind === 'phone-au') {
      var digits = value.replace(/[\s().\-–—]/g, '');
      return AU_PHONE_RE.test(digits)
        ? ''
        : field.dataset.qrMessage || 'Enter a valid Australian phone number.';
    }

    return '';
  }

  // Push our verdict into the browser's own validity state, so the native
  // bubble, :invalid styling and checkValidity() all agree with us.
  function syncValidity(field) {
    if (!field.dataset.qrValidate || typeof field.setCustomValidity !== 'function') return;
    field.setCustomValidity(customMessage(field));
  }

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event };
    for (var k in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, k)) data[k] = payload[k];
    }
    window.dataLayer.push(data);
  }

  function store(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (err) {
      /* private browsing — state just won't persist */
    }
  }

  function read(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function init(root) {
    if (!root || root.dataset.qrReady === 'true') return;
    root.dataset.qrReady = 'true';

    var form = root.querySelector('form');
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-qr-step]'));
    var progressBtns = Array.prototype.slice.call(root.querySelectorAll('[data-qr-goto]'));
    var results = root.querySelector('[data-qr-results]');
    var placeholder = root.querySelector('[data-qr-placeholder]');
    var collections = Array.prototype.slice.call(root.querySelectorAll('[data-qr-collection]'));
    var defaultPanel = root.querySelector('[data-qr-default]');
    var success = root.querySelector('[data-qr-success]');

    if (!form || !steps.length) return;

    var key = STORAGE_PREFIX + root.id;
    var autoAdvance = root.dataset.qrAutoAdvance === 'true';

    // Step roles come from data attributes, not hardcoded numbers, so the
    // steps can be reordered in the Liquid without touching this file.
    var q1Step = root.querySelector('[data-qr-q1]');
    var PRODUCT_STEP = q1Step
      ? Number(q1Step.closest('[data-qr-step]').dataset.qrStep)
      : 1;

    var ANSWER_KEYS = ['q1', 'q2', 'q3'];

    function answerKey(n) {
      var el = stepEl(n);
      return el && el.dataset.qrAnswerKey ? el.dataset.qrAnswerKey : null;
    }

    var furthest = 1;
    var current = 0;

    // We validate step by step, so switch off native full-form validation.
    // Without JS this attribute is never added and the browser validates normally.
    form.setAttribute('novalidate', 'novalidate');

    /* ---------------- collection panel ---------------- */

    // Q1 answered: the curated default set is REPLACED by the match.
    function showCollection(target) {
      collections.forEach(function (el) {
        el.hidden = !(target === '__all__' || el.dataset.qrCollection === target);
      });
      if (defaultPanel) defaultPanel.hidden = true;
      if (placeholder) placeholder.hidden = true;
    }

    // Default state: curated mix visible, every mapped collection hidden.
    function showDefault() {
      collections.forEach(function (el) {
        el.hidden = true;
      });
      if (defaultPanel) defaultPanel.hidden = false;
      if (placeholder) placeholder.hidden = !!defaultPanel;
    }

    /* ---------------- steps ---------------- */

    function stepEl(n) {
      return steps.filter(function (s) {
        return Number(s.dataset.qrStep) === n;
      })[0];
    }

    function fieldsIn(n) {
      var el = stepEl(n);
      if (!el) return [];
      return Array.prototype.slice.call(el.querySelectorAll('input, select, textarea'));
    }

    // Returns the first invalid field in a step, or null.
    // A radio group counts as valid if any one of its buttons is checked.
    // Pure check — safe to run against a hidden step.
    function firstInvalid(n) {
      var fields = fieldsIn(n);
      var seenRadioGroups = {};

      for (var i = 0; i < fields.length; i++) {
        var f = fields[i];
        if (f.type === 'radio') {
          if (seenRadioGroups[f.name]) continue;
          seenRadioGroups[f.name] = true;
          var group = fields.filter(function (x) {
            return x.name === f.name;
          });
          var checked = group.some(function (x) {
            return x.checked;
          });
          if (!checked) return group[0];
        } else {
          syncValidity(f);
          if (!f.checkValidity()) return f;
        }
      }
      return null;
    }

    // Same check, but reports the problem to the visitor. Only call this on
    // the step that is currently on screen — a hidden field cannot be focused.
    function validateStep(n) {
      var bad = firstInvalid(n);
      flagStep(n, !!bad);
      if (!bad) return true;
      bad.focus();
      if (bad.type !== 'radio') bad.reportValidity();
      return false;
    }

    function flagStep(n, invalid) {
      var el = stepEl(n);
      if (el) el.classList.toggle('has-error', !!invalid);
    }

    function answerFor(n) {
      var checked = fieldsIn(n).filter(function (f) {
        return f.type === 'radio' && f.checked;
      })[0];
      return checked ? checked.value : '';
    }

    function updateProgress() {
      progressBtns.forEach(function (btn) {
        var n = Number(btn.dataset.qrGoto);
        var item = btn.closest('[data-qr-progress-item]');
        var done = n < furthest;
        btn.disabled = n > furthest;
        if (item) {
          item.classList.toggle('is-active', n === current);
          item.classList.toggle('is-done', done);
        }
        btn.setAttribute('aria-current', n === current ? 'step' : 'false');
      });
    }

    function goTo(n, opts) {
      opts = opts || {};
      if (n < 1 || n > steps.length) return;

      steps.forEach(function (s) {
        var active = Number(s.dataset.qrStep) === n;
        s.classList.toggle('is-active', active);
        s.hidden = !active;
      });

      current = n;
      if (n > furthest) furthest = n;
      updateProgress();
      store(key + ':step', String(n));

      // The panel is always on screen: curated default until Q1 is answered,
      // the matching collection afterwards. Only the fallback placeholder
      // (used when no default set is configured) waits for the product step.
      if (results) results.hidden = false;
      if (placeholder && !defaultPanel) {
        var anySelected = collections.some(function (el) {
          return !el.hidden;
        });
        placeholder.hidden = anySelected || n < PRODUCT_STEP;
      }

      var el = stepEl(n);
      var title = el && el.querySelector('.qr__step-title');
      if (!opts.silent && title) title.focus({ preventScroll: true });

      if (!opts.silent) {
        push('qr_step_view', {
          qr_step: n,
          qr_step_name: el ? el.dataset.qrStepName : ''
        });
      }

      if (!opts.noScroll && el) {
        var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
        if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
      }
    }

    function next() {
      if (!validateStep(current)) return;
      var el = stepEl(current);
      push('qr_step_complete', {
        qr_step: current,
        qr_step_name: el ? el.dataset.qrStepName : '',
        qr_answer: answerFor(current)
      });
      var ak = answerKey(current);
      if (ak) store(key + ':' + ak, answerFor(current));
      goTo(current + 1);
    }

    /* ---------------- wiring ---------------- */

    root.querySelectorAll('[data-qr-next]').forEach(function (btn) {
      btn.addEventListener('click', next);
    });

    root.querySelectorAll('[data-qr-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goTo(current - 1);
      });
    });

    progressBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = Number(btn.dataset.qrGoto);
        if (n <= furthest) goTo(n);
      });
    });

    // Re-check as they type so a stale error clears itself, and again on blur
    // so a wrong number is flagged before they reach the Continue button.
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-qr-validate]'),
      function (field) {
        field.addEventListener('input', function () {
          field.setCustomValidity('');
        });
        field.addEventListener('blur', function () {
          syncValidity(field);
          if (field.value.trim() && !field.checkValidity()) field.reportValidity();
        });
      }
    );

    // Enter in a text field advances instead of submitting a half-filled form.
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var t = e.target;
      if (t.tagName === 'TEXTAREA' || t.type === 'submit') return;
      if (current < steps.length) {
        e.preventDefault();
        next();
      }
    });

    // Q1 drives the panel the instant an option is picked.
    var q1Radios = Array.prototype.slice.call(
      root.querySelectorAll('[data-qr-q1] input[type="radio"]')
    );

    q1Radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        showCollection(radio.dataset.qrTarget);
        flagStep(PRODUCT_STEP, false);
        store(key + ':q1', radio.value);
      });
    });

    // Optional auto-advance on the quantity and timeline steps only.
    // The product step deliberately waits so the panel below can be seen.
    if (autoAdvance) {
      steps.forEach(function (s) {
        if (s.dataset.qrAuto !== 'true') return;
        var n = Number(s.dataset.qrStep);
        fieldsIn(n).forEach(function (f) {
          if (f.type !== 'radio') return;
          f.addEventListener('change', function () {
            if (current === n && n < steps.length) window.setTimeout(next, 220);
          });
        });
      });
    }

    // Final guard: check every step, jump to the first that fails.
    // firstInvalid() is a pure check, so hidden steps are inspected safely;
    // only after switching to the offending step do we focus and report.
    form.addEventListener('submit', function (e) {
      for (var n = 1; n <= steps.length; n++) {
        if (firstInvalid(n)) {
          e.preventDefault();
          goTo(n);
          validateStep(n);
          push('qr_validation_error', {
            qr_step: n,
            qr_step_name: stepEl(n).dataset.qrStepName
          });
          return;
        }
      }
      push('qr_submit', {
        qr_product: read(key + ':q1') || '',
        qr_quantity: read(key + ':q2') || '',
        qr_timeline: read(key + ':q3') || ''
      });
    });

    /* ---------------- initial state ---------------- */

    if (success) {
      // Submitted successfully. Replace the form with the thank-you message,
      // but KEEP the products on screen — they are the reason to stay on the
      // page, and the range they just enquired about is the most relevant
      // thing to browse next.
      var submittedQ1 = read(key + ':q1');
      var submittedMatch = submittedQ1
        ? q1Radios.filter(function (r) {
            return r.value === submittedQ1;
          })[0]
        : null;

      if (submittedMatch) {
        showCollection(submittedMatch.dataset.qrTarget);
      } else {
        showDefault();
      }

      ['step'].concat(ANSWER_KEYS).forEach(function (k) {
        store(key + ':' + k, null);
      });

      steps.forEach(function (s) {
        s.hidden = true;
      });
      var progress = root.querySelector('[data-qr-progress]');
      if (progress) progress.hidden = true;
      if (results) results.hidden = false;

      success.focus();
      push('qr_success', { qr_product: submittedQ1 || '' });
      return;
    }

    showDefault();

    // Saved answers exist only to survive a REJECTED form post, where Shopify
    // reloads the page and wipes the radios. On any ordinary page view we
    // start clean — otherwise a visitor who picked an option earlier in the
    // session never sees the default state again.
    var returningFromPost = root.dataset.qrHasErrors === 'true';

    if (!returningFromPost) {
      ['step'].concat(ANSWER_KEYS).forEach(function (k) {
        store(key + ':' + k, null);
      });
      goTo(1, { noScroll: true });
      return;
    }

    // Post rejected (usually a malformed email): put the answers back and
    // land on the details step, which is where the error is.
    var savedQ1 = read(key + ':q1');
    if (savedQ1) {
      var match = q1Radios.filter(function (r) {
        return r.value === savedQ1;
      })[0];
      if (match) {
        match.checked = true;
        showCollection(match.dataset.qrTarget);
      }
    }

    steps.forEach(function (s) {
      var ak = s.dataset.qrAnswerKey;
      if (!ak || ak === 'q1') return;
      var saved = read(key + ':' + ak);
      if (!saved) return;
      fieldsIn(Number(s.dataset.qrStep)).forEach(function (f) {
        if (f.type === 'radio' && f.value === saved) f.checked = true;
      });
    });

    furthest = steps.length;
    goTo(steps.length, { noScroll: true });
  }

  function initAll() {
    document.querySelectorAll('[data-qr-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-qr-root]');
    if (root) init(root);
  });
})();
